import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import type { SelectOption } from "@cinnamon/design-system/types";
import { API_PATH } from "@constants/config";
import { request } from "@helper/http";

import { FORM_TYPE } from "@/constants/global";
import { normalizePipeline, normalizePipelineVersion, transformDetectableClassesToPayload } from "@/converter/pipeline";
import { invalidatesOn } from "@/helper/invalidatesOn";
import { FORMAT_TEXT_TYPE_DATA_ENHANCEMENT } from "@/types/fixForm";
import { type APIQuery, type APIResponse } from "@/types/global";
import {
  type AutoClassificationClassConfig,
  type AutoClassificationClassListItem,
  type AutoClassificationKeywordMatchingServer,
  type AutoClassificationPipeline,
  type ClassificationPipeline,
  type ConvertDateFormatValue,
  type CreateAutoClassificationClassPayload,
  type CreateAutoClassificationClassResponse,
  type DataEnhancementServer,
  type DetectableClass,
  type DetectableClassServer,
  type Document,
  type IDataEnhancement,
  type IFieldInfo,
  type LLM_AI_SETTING_MODELS,
  type OcrPipeline,
  PIPELINES_LIMIT_DEFAULT,
  PIPELINES_OFFSET_DEFAULT,
  type PipelineBasicInfo,
  type PipelineId,
  type PipelineVersion,
  type PipelineVersionId,
  STEP_TYPE_DATA_ENHANCEMENT,
  type ServerClassificationPipeline,
  type ServerOcrPipeline,
  type TargetId,
  type UpdateAutoClassificationClassPayload,
  type UpdateAutoClassificationClassesPriorityPayload,
  type UpdateAutoClassificationKeywordMatchingPayload,
} from "@/types/pipeline";
import { type Template } from "@/types/template";

export async function getPipelines(params: APIQuery): Promise<APIResponse<PipelineBasicInfo>> {
  const result = await request({
    url: API_PATH.PIPELINES,
    method: "get",
    params,
  });
  return {
    ...result,
    results: result.results.map(
      (serverPipeline: ServerOcrPipeline | ServerClassificationPipeline | AutoClassificationPipeline) => {
        return normalizePipeline(serverPipeline);
      }
    ),
  };
}

export async function getAllPipeline(params: APIQuery): Promise<APIResponse<PipelineBasicInfo>> {
  let offset = PIPELINES_OFFSET_DEFAULT;
  const limit = PIPELINES_LIMIT_DEFAULT;
  try {
    const result = await getPipelines({ ...params, offset, limit });
    let normalizedResult = result;

    while (normalizedResult.next) {
      offset += limit;
      normalizedResult = await getPipelines({ ...params, offset, limit });
      result.results = [...result.results, ...normalizedResult.results];
    }

    return {
      ...result,
    };
  } catch (error) {
    return Promise.reject(error);
  }
}

export const getPipelineDetail = async (
  id: PipelineId
): Promise<ClassificationPipeline | AutoClassificationPipeline | OcrPipeline> => {
  const result = await request({
    url: `${API_PATH.PIPELINES}${id}/`,
    method: "get",
  });

  return normalizePipeline(result);
};

export const getPipelineVersionLatest = async ({ id }: { id: PipelineId }): Promise<PipelineVersion> => {
  const result = await request({
    url: API_PATH.PIPELINES_VERSION_LATEST(id),
    method: "get",
  });

  return normalizePipelineVersion(result);
};

interface DraftPipelineVersionParams {
  id: PipelineVersionId;
  formType: FORM_TYPE;
}

export const draftPipelineVersion = async ({ id, formType }: DraftPipelineVersionParams): Promise<PipelineVersion> => {
  let url = API_PATH.PIPELINE_VERSION_DRAFT(id);

  switch (formType) {
    case FORM_TYPE.MANUAL_CLASSIFICATION:
      url = API_PATH.MANUAL_CLASSIFICATION_DRAFT(id);
      break;
    case FORM_TYPE.AUTO_CLASSIFICATION:
      url = API_PATH.AUTO_CLASSIFICATION_DRAFT(id);
      break;
    default:
      break;
  }

  const result = await request({
    url,
    method: "post",
  });

  return normalizePipelineVersion(result);
};

export const getFixedFormTemplateById = (
  pipelineId?: string,
  options?: Record<string, string>
): Promise<APIResponse<Template>> => {
  const params = pipelineId ? { pipeline__id: pipelineId } : {};
  return request({
    url: `${API_PATH.FIXED_TEMPLATES}`,
    method: "get",
    params: { ...params, ...(options || {}) },
  });
};

export const rerunFixedForm = (jobJd: string, newFixedTemplate: string): Promise<void> => {
  return request({
    url: `${API_PATH.FIXED_FORM_RERUN(jobJd)}`,
    method: "post",
    data: { new_fixed_template: newFixedTemplate },
  });
};

export const createNewPipeline = (data: Partial<PipelineBasicInfo>): Promise<PipelineBasicInfo> => {
  const url =
    data.form_type === FORM_TYPE.MANUAL_CLASSIFICATION ? API_PATH.MANUAL_CLASSIFICATION_CREATE : API_PATH.PIPELINES;

  return request({
    url,
    method: "post",
    data,
  });
};

export const deletePipeline = (id: string, formType: FORM_TYPE): Promise<void> => {
  const url =
    formType === FORM_TYPE.MANUAL_CLASSIFICATION
      ? API_PATH.MANUAL_CLASSIFICATION_DELETE(id)
      : `${API_PATH.PIPELINES}${id}/`;

  return request({
    url,
    method: "delete",
  });
};

export interface UpdatePipelinePayload {
  id: string;
  data: Partial<OcrPipeline | ClassificationPipeline | AutoClassificationPipeline>;
}

export const updatePipeline = (payload: UpdatePipelinePayload): Promise<PipelineBasicInfo> => {
  const { id, data } = payload;

  return request({
    url: `${API_PATH.PIPELINES}${id}/`,
    method: "patch",
    data,
  });
};

export interface UpdateAllowRegularUsersLlmSamplesData {
  enable_allow_regular_users_to_register_llm_samples: boolean;
}

export interface UpdateAllowRegularUsersLlmSamplesPayload {
  id: PipelineId;
  data: UpdateAllowRegularUsersLlmSamplesData;
}

export const updateAllowRegularUsersLlmSamples = ({
  id,
  data,
}: UpdateAllowRegularUsersLlmSamplesPayload): Promise<PipelineBasicInfo> => {
  return request({
    url: API_PATH.ALLOW_REGULAR_USERS_LLM_SAMPLES(id),
    method: "PATCH",
    data,
  });
};

export enum TargetAction {
  ADD = "add",
  EDIT = "edit",
  DELETE = "delete",
}

export interface TargetPayload extends Partial<Document> {
  code_name?: string;
  alternative_name?: string[];
}

export interface UpdateTargetPayload {
  action: TargetAction;
  id?: TargetId;
  payload?: TargetPayload;
}

export interface UpdateTargetsData {
  id: PipelineVersionId;
  targets: UpdateTargetPayload[];
}

export const updateTargets = (payload: UpdateTargetsData): Promise<void> => {
  const { id, targets } = payload;

  return request({
    url: `${API_PATH.PIPELINE_VERSION_TARGETS(id)}`,
    method: "post",
    data: { targets },
  });
};

export const updateClassesConfigManualClassification = (
  pipelineVersionId: PipelineVersionId,
  detectableClasses: DetectableClassServer[]
): Promise<PipelineBasicInfo> => {
  return request({
    url: `${API_PATH.MANUAL_CLASSIFICATION_VERSION_CONFIG(pipelineVersionId)}`,
    method: "put",
    data: { detectable_classes: detectableClasses },
  });
};

export const updateClassesConfigAutoClassification = (
  pipelineVersionId: PipelineVersionId,
  classConfigs: AutoClassificationClassConfig[]
): Promise<PipelineBasicInfo> => {
  return request({
    url: `${API_PATH.AUTO_CLASSIFICATION_VERSION_CONFIG(pipelineVersionId)}`,
    method: "put",
    data: { class_configs: classConfigs },
  });
};

export async function getAutoClassificationClasses(
  pipelineVersionId: PipelineVersionId
): Promise<AutoClassificationClassListItem[]> {
  const result = await request({
    url: API_PATH.AUTO_CLASSIFICATION_VERSION_CLASSES(pipelineVersionId),
    method: "get",
  });
  return result;
}

export async function createAutoClassificationClass(
  pipelineVersionId: PipelineVersionId,
  payload: CreateAutoClassificationClassPayload
): Promise<CreateAutoClassificationClassResponse> {
  const result = await request({
    url: API_PATH.AUTO_CLASSIFICATION_VERSION_CLASSES(pipelineVersionId),
    method: "post",
    data: payload,
  });
  return result;
}

export async function deleteAutoClassificationClass(
  pipelineVersionId: PipelineVersionId,
  classId: string
): Promise<void> {
  await request({
    url: API_PATH.AUTO_CLASSIFICATION_VERSION_CLASS(pipelineVersionId, classId),
    method: "delete",
  });
}

export async function updateAutoClassificationClass(
  pipelineVersionId: PipelineVersionId,
  classId: string,
  payload: UpdateAutoClassificationClassPayload
): Promise<AutoClassificationClassListItem> {
  const result = await request({
    url: API_PATH.AUTO_CLASSIFICATION_VERSION_CLASS(pipelineVersionId, classId),
    method: "patch",
    data: payload,
  });
  return result;
}

export async function updateAutoClassificationClassesPriority(
  pipelineVersionId: PipelineVersionId,
  payload: UpdateAutoClassificationClassesPriorityPayload
): Promise<AutoClassificationClassListItem[]> {
  const result = await request({
    url: API_PATH.AUTO_CLASSIFICATION_VERSION_CLASSES_UPDATE_PRIORITY(pipelineVersionId),
    method: "post",
    data: payload,
  });
  return result;
}

export async function updateAutoClassificationKeywordMatchingConfig(
  pipelineVersionId: PipelineVersionId,
  classId: string,
  payload: UpdateAutoClassificationKeywordMatchingPayload
): Promise<AutoClassificationKeywordMatchingServer> {
  const result = await request({
    url: API_PATH.AUTO_CLASSIFICATION_VERSION_CLASS_KEYWORD_MATCHING(pipelineVersionId, classId),
    method: "patch",
    data: payload,
  });
  return result;
}

interface IPayloadPostProcessorsPipeline {
  data: IDataEnhancement[];
}

const outputPayloadPipelineProcessor = (params: {
  input: IFieldInfo[];
  output: IFieldInfo[];
  action: STEP_TYPE_DATA_ENHANCEMENT | null;
}): string[] => {
  const { input, output, action } = params;
  if (!output.length) return [];
  if (action === STEP_TYPE_DATA_ENHANCEMENT.FORMAT_TEXT || input.length === 1) {
    return [output[0].id];
  } else {
    return output.map((item) => item.id);
  }
};
const formatPayloadPipelineProcessor = (payload: IPayloadPostProcessorsPipeline): DataEnhancementServer[] => {
  const { data } = payload;
  return data.map((config): DataEnhancementServer => {
    let value: DataEnhancementServer = {
      input: config.input.map((item) => item.id),
      output: outputPayloadPipelineProcessor({
        input: config.input,
        output: config.output,
        action: config.action,
      }),
    };
    switch (config.action) {
      case STEP_TYPE_DATA_ENHANCEMENT.FIND_AND_REPLACE:
      case STEP_TYPE_DATA_ENHANCEMENT.FIND_AND_REPLACE_REGEX: {
        value = {
          ...value,
          find: config.value[0] as string[],
          replace: (config.value[1] as string[])[0] || "",
        };
        break;
      }
      case STEP_TYPE_DATA_ENHANCEMENT.FORMAT_TEXT: {
        const { value: rawValues } = config;

        value = {
          ...value,
          template: rawValues.map((rawValue) => {
            if (typeof rawValue === "string") {
              return { type: FORMAT_TEXT_TYPE_DATA_ENHANCEMENT.LITERAL, value: rawValue };
            } else {
              return {
                type: FORMAT_TEXT_TYPE_DATA_ENHANCEMENT.PLACEHOLDER,
                value: (rawValue as SelectOption).value as string,
              };
            }
          }),
        };
        break;
      }

      case STEP_TYPE_DATA_ENHANCEMENT.CONVERT_DATE_FORMAT: {
        const configValue = config.value as ConvertDateFormatValue;
        const dateFormat = (configValue[0] as SelectOption).value as string;
        const returnOriginalIfFails = configValue[1] === "true" ? true : false;
        const completeMissingYear = configValue[2] ? configValue[2] : null;

        value = {
          ...value,
          date_format: dateFormat,
          return_original_if_fails: returnOriginalIfFails,
          complete_missing_year: completeMissingYear,
        };
        break;
      }

      case STEP_TYPE_DATA_ENHANCEMENT.CONVERT_TO_UPPER_CASE:
      case STEP_TYPE_DATA_ENHANCEMENT.CONVERT_TO_LOWER_CASE: {
        value = {
          ...value,
        };
        break;
      }

      case STEP_TYPE_DATA_ENHANCEMENT.CONVERT_BYTE_STRING: {
        value = {
          ...value,
          mode: config.value[0],
        };
        break;
      }

      default: {
        // Trim Spaces
        value = {
          ...value,
          remove_all: config.value?.[0] === "true" ? true : false,
        };
      }
    }

    return {
      action: config.action,
      description: config.description,
      ...value,
    };
  });
};

export const handlePostProcessorsPipelineVersion = (
  id: PipelineVersionId,
  steps: DataEnhancementServer[]
): Promise<APIResponse<void>> => {
  return request({
    url: `${API_PATH.PIPELINE_VERSION_POST_PROCESSORS(id)}`,
    method: "PUT",
    data: {
      steps,
    },
  });
};

interface PublishDiscardPipelineVersionPayload {
  id: PipelineVersionId;
  release_note?: string;
  formType: FORM_TYPE;
}

const onPublishPipelineVersion = ({
  id,
  release_note: releaseNote,
  formType,
}: PublishDiscardPipelineVersionPayload): Promise<void> => {
  let url: string;

  switch (formType) {
    case FORM_TYPE.MANUAL_CLASSIFICATION:
      url = API_PATH.MANUAL_CLASSIFICATION_VERSION_PUBLISH(id);
      break;
    case FORM_TYPE.AUTO_CLASSIFICATION:
      url = API_PATH.AUTO_CLASSIFICATION_VERSION_PUBLISH(id);
      break;
    default:
      url = API_PATH.PIPELINE_VERSION_PUBLISH(id);
  }

  return request({
    url,
    method: "POST",
    data: { release_note: releaseNote || undefined },
  });
};

const onDiscardPipelineVersion = ({ id, formType }: PublishDiscardPipelineVersionPayload): Promise<void> => {
  let url: string;

  switch (formType) {
    case FORM_TYPE.MANUAL_CLASSIFICATION:
      url = API_PATH.MANUAL_CLASSIFICATION_VERSION_DISCARD(id);
      break;
    case FORM_TYPE.AUTO_CLASSIFICATION:
      url = API_PATH.AUTO_CLASSIFICATION_VERSION_DISCARD(id);
      break;
    default:
      url = API_PATH.PIPELINE_VERSION_DISCARD(id);
  }

  return request({
    url,
    method: "POST",
  });
};

export interface UpdatePipelineVersionExtractionConfigData {
  enable_single_field_extraction?: boolean;
  enable_table_field_extraction?: boolean;

  llm_model_type?: LLM_AI_SETTING_MODELS | null;
  single_fields_llm_model_type?: string | null;
  table_fields_llm_model_type?: string | null;
}
interface UpdatePipelineVersionExtractionConfig {
  id: PipelineId;

  data: UpdatePipelineVersionExtractionConfigData;
}

const updatePipelineVersionExtractionConfig = ({ id, data }: UpdatePipelineVersionExtractionConfig): Promise<void> => {
  return request({
    url: `${API_PATH.PIPELINE_VERSION_EXTRACTION_CONFIG(id)}`,
    method: "PATCH",
    data,
  });
};

export const pipelineApi = createApi({
  reducerPath: "pipelineApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Pipelines", "pipelineDetail", "PipelineVersions", "AutoClassificationClasses"],
  endpoints: (build) => ({
    getPipelines: build.query({
      queryFn: async (params: APIQuery) => {
        try {
          const data = await getPipelines(params);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["Pipelines"],
    }),
    getPipelineDetail: build.query({
      queryFn: async (id: PipelineId) => {
        try {
          const data = await getPipelineDetail(id);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["pipelineDetail"],
    }),
    getPipelineVersionLatest: build.query({
      queryFn: async ({ id }: { id: PipelineId }) => {
        try {
          const data = await getPipelineVersionLatest({ id });
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["PipelineVersions"],
    }),
    draftPipelineVersion: build.mutation({
      queryFn: async (params: DraftPipelineVersionParams) => {
        try {
          const data = await draftPipelineVersion(params);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({
        success: ["PipelineVersions", "AutoClassificationClasses"],
        error: [],
      }),
    }),
    handlePostProcessorsPipelineVersion: build.mutation({
      queryFn: async (payload: { id: PipelineVersionId; data: IDataEnhancement[] }) => {
        try {
          const { id, data } = payload;
          const payloadFormatted = formatPayloadPipelineProcessor({ data });
          const result = await handlePostProcessorsPipelineVersion(id, payloadFormatted);
          return { data: result };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["PipelineVersions", "pipelineDetail"], error: [] }),
    }),
    createNewPipeline: build.mutation({
      queryFn: async (payload: Partial<PipelineBasicInfo> & { form_type: FORM_TYPE }) => {
        try {
          const data = await createNewPipeline(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Pipelines"], error: [] }),
    }),
    deletePipeline: build.mutation({
      queryFn: async ({ pipelineId, formType }: { pipelineId: string; formType: FORM_TYPE }) => {
        try {
          const data = await deletePipeline(pipelineId, formType);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Pipelines"], error: [] }),
    }),
    updatePipeline: build.mutation({
      queryFn: async (payload: UpdatePipelinePayload) => {
        try {
          const data = await updatePipeline(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["pipelineDetail", "Pipelines"], error: [] }),
    }),
    updateAllowRegularUsersLlmSamples: build.mutation({
      queryFn: async (payload: UpdateAllowRegularUsersLlmSamplesPayload) => {
        try {
          const data = await updateAllowRegularUsersLlmSamples(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["pipelineDetail"], error: [] }),
    }),
    updateTargets: build.mutation({
      queryFn: async (payload: UpdateTargetsData) => {
        try {
          const data = await updateTargets(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["pipelineDetail", "PipelineVersions"], error: [] }),
    }),
    updateClassesConfigManualClassification: build.mutation({
      queryFn: async (payload: { pipelineVersionId: string; detectableClasses: DetectableClass[] }) => {
        try {
          const { pipelineVersionId, detectableClasses } = payload;

          const data = await updateClassesConfigManualClassification(
            pipelineVersionId,
            transformDetectableClassesToPayload(detectableClasses)
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["PipelineVersions"], error: [] }),
    }),
    updateClassesConfigAutoClassification: build.mutation({
      queryFn: async (payload: { pipelineVersionId: string; classConfigs: AutoClassificationClassConfig[] }) => {
        try {
          const { pipelineVersionId, classConfigs } = payload;

          const data = await updateClassesConfigAutoClassification(pipelineVersionId, classConfigs);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["PipelineVersions", "AutoClassificationClasses"], error: [] }),
    }),
    getAutoClassificationClasses: build.query({
      queryFn: async (
        id: PipelineVersionId
      ): Promise<{ data: AutoClassificationClassListItem[] } | { error: unknown }> => {
        try {
          const data = await getAutoClassificationClasses(id);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["AutoClassificationClasses"],
    }),
    createAutoClassificationClass: build.mutation({
      queryFn: async (payload: {
        pipelineVersionId: string;
        data: CreateAutoClassificationClassPayload;
      }): Promise<{ data: CreateAutoClassificationClassResponse } | { error: unknown }> => {
        try {
          const { pipelineVersionId, data } = payload;
          const result = await createAutoClassificationClass(pipelineVersionId, data);
          return { data: result };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["AutoClassificationClasses"], error: [] }),
    }),
    deleteAutoClassificationClass: build.mutation({
      queryFn: async (payload: {
        pipelineVersionId: string;
        classId: string;
      }): Promise<{ data: void } | { error: unknown }> => {
        try {
          const { pipelineVersionId, classId } = payload;
          await deleteAutoClassificationClass(pipelineVersionId, classId);
          return { data: undefined };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["AutoClassificationClasses"], error: [] }),
    }),
    updateAutoClassificationClass: build.mutation({
      queryFn: async (payload: {
        pipelineVersionId: string;
        classId: string;
        data: UpdateAutoClassificationClassPayload;
      }): Promise<{ data: AutoClassificationClassListItem } | { error: unknown }> => {
        try {
          const { pipelineVersionId, classId, data } = payload;
          const result = await updateAutoClassificationClass(pipelineVersionId, classId, data);
          return { data: result };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["AutoClassificationClasses"], error: [] }),
    }),
    updateAutoClassificationClassesPriority: build.mutation({
      queryFn: async (payload: {
        pipelineVersionId: string;
        data: UpdateAutoClassificationClassesPriorityPayload;
      }): Promise<{ data: AutoClassificationClassListItem[] } | { error: unknown }> => {
        try {
          const { pipelineVersionId, data } = payload;
          const result = await updateAutoClassificationClassesPriority(pipelineVersionId, data);
          return { data: result };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: [], error: [] }),
    }),
    updateAutoClassificationKeywordMatchingConfig: build.mutation({
      queryFn: async (payload: {
        pipelineVersionId: string;
        classId: string;
        data: UpdateAutoClassificationKeywordMatchingPayload;
      }): Promise<{ data: AutoClassificationKeywordMatchingServer } | { error: unknown }> => {
        try {
          const { pipelineVersionId, classId, data } = payload;
          const result = await updateAutoClassificationKeywordMatchingConfig(pipelineVersionId, classId, data);
          return { data: result };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["AutoClassificationClasses"], error: [] }),
    }),
    publishPipelineVersion: build.mutation({
      queryFn: async (payload: PublishDiscardPipelineVersionPayload) => {
        try {
          const data = await onPublishPipelineVersion(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({
        success: ["pipelineDetail", "PipelineVersions", "Pipelines", "AutoClassificationClasses"],
        error: [],
      }),
    }),
    discardPipelineVersion: build.mutation({
      queryFn: async (payload: PublishDiscardPipelineVersionPayload) => {
        try {
          const data = await onDiscardPipelineVersion(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({
        success: ["pipelineDetail", "PipelineVersions", "Pipelines", "AutoClassificationClasses"],
        error: [],
      }),
    }),
    updatePipelineVersionExtractionConfig: build.mutation({
      queryFn: async (payload: UpdatePipelineVersionExtractionConfig) => {
        try {
          const data = await updatePipelineVersionExtractionConfig(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["pipelineDetail", "PipelineVersions", "Pipelines"], error: [] }),
    }),
  }),
});

export const {
  useGetPipelinesQuery,
  useGetPipelineDetailQuery,

  useGetPipelineVersionLatestQuery,

  useDraftPipelineVersionMutation,

  useHandlePostProcessorsPipelineVersionMutation,

  useCreateNewPipelineMutation,
  useDeletePipelineMutation,
  useUpdatePipelineMutation,
  useUpdateAllowRegularUsersLlmSamplesMutation,
  useUpdateTargetsMutation,
  useUpdateClassesConfigManualClassificationMutation,
  useUpdateClassesConfigAutoClassificationMutation,
  usePublishPipelineVersionMutation,
  useDiscardPipelineVersionMutation,
  useUpdatePipelineVersionExtractionConfigMutation,
  useGetAutoClassificationClassesQuery,
  useCreateAutoClassificationClassMutation,
  useDeleteAutoClassificationClassMutation,
  useUpdateAutoClassificationClassMutation,
  useUpdateAutoClassificationClassesPriorityMutation,
  useUpdateAutoClassificationKeywordMatchingConfigMutation,
} = pipelineApi;
